import { Phone, ArrowRight, CalendarCheck } from "lucide-react";
import { APPLY_URL, BOOK_URL, PHONE_NUMBER, PHONE_TEL, PRMG_LOGO, HEADSHOT_IMAGE, LO_NAME } from "@/lib/site";
import { useEffect, useState } from "react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full backdrop-blur-md transition-all duration-200 ${
        scrolled
          ? "bg-white/95 border-b border-[#E2E8F0] shadow-sm"
          : "bg-white/80 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 md:gap-6">
          <div data-testid="header-logo" className="flex items-center gap-3 md:gap-4">
            <a href="#top" className="flex items-center gap-3 md:gap-4 group">
              <img
                src={PRMG_LOGO}
                alt="PRMG — Paramount Residential Mortgage Group"
                className="h-14 md:h-16 w-auto"
              />
            </a>

            <a
              href="#officer"
              data-testid="header-officer-card"
              className="hidden md:flex items-center gap-3 pl-4 border-l border-[#E2E8F0] hover:opacity-90 transition-opacity"
            >
              <img
                src={HEADSHOT_IMAGE}
                alt={`${LO_NAME} — Loan Officer`}
                className="h-12 w-12 rounded-full object-cover ring-2 ring-[#F26A21]/20"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-display font-bold text-[#0A2F6B] text-sm whitespace-nowrap">
                  {LO_NAME}
                </span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-[#475569] font-semibold whitespace-nowrap">
                  Loan Officer · NMLS&nbsp;#2685815
                </span>
              </div>
            </a>
          </div>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-[#0F172A] pl-4 border-l border-[#E2E8F0]">
            <a href="#uses" data-testid="nav-uses" className="hover:text-[#F26A21] transition-colors whitespace-nowrap">HELOC Uses</a>
            <a href="#faq" data-testid="nav-faq" className="hover:text-[#F26A21] transition-colors">FAQ</a>
            <a href="#contact" data-testid="nav-contact" className="hover:text-[#F26A21] transition-colors">Contact</a>
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={PHONE_TEL}
            data-testid="header-phone-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#0A2F6B] hover:text-[#F26A21] transition-colors px-3 py-2 rounded-md whitespace-nowrap"
          >
            <Phone size={16} strokeWidth={2.5} />
            <span className="tabular-nums">{PHONE_NUMBER}</span>
          </a>

          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-button"
            className="hidden md:inline-flex items-center gap-2 bg-white hover:bg-[#F1F5F9] text-[#0A2F6B] border border-[#0A2F6B]/15 hover:border-[#0A2F6B]/40 font-semibold text-sm px-4 md:px-5 py-2.5 rounded-md shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
          >
            <CalendarCheck size={16} strokeWidth={2.5} />
            Book a Meeting
          </a>

          <a
            href={APPLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-apply-button"
            className="inline-flex items-center gap-2 bg-[#F26A21] hover:bg-[#D95A1A] text-white font-semibold text-sm px-4 md:px-5 py-2.5 rounded-md shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
          >
            Apply Now
            <ArrowRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </header>
  );
}
